import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { Search, Filter, ChevronLeft, ChevronRight } from "lucide-react";
import CartIcon from "../../components/common/CartIcon";

const apiUrl = import.meta.env.VITE_APP_API;

function BookStore() {
    const [categories, setCategories] = useState([]);
    const [books, setBooks] = useState([]);
    const [filteredBooks, setFilteredBooks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchLoading, setSearchLoading] = useState(false);
    const [error, setError] = useState(null);

    // Filter and search states
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [showFilters, setShowFilters] = useState(false);

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [booksPerPage] = useState(12);

    useEffect(() => {
        fetchCategories();
    }, []);

    useEffect(() => {
        fetchBooks();
    }, [selectedCategory, debouncedSearchTerm]);

    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500); // 500ms delay

        return () => clearTimeout(timer);
    }, [searchTerm]);

    const fetchCategories = async () => {
        try {
            const categoriesResponse = await axios.get(`${apiUrl}/api/bookstore/categories/`);
            const categoriesData = categoriesResponse.data.results || categoriesResponse.data;
            setCategories(categoriesData);
        } catch (error) {
            console.error('Error fetching categories:', error);
            toast.error('Failed to load categories');
        }
    };

    const fetchBooks = async () => {
        try {
            // Show search loading only if we're searching, not for initial load
            if (debouncedSearchTerm.trim()) {
                setSearchLoading(true);
            } else {
                setLoading(true);
            }
            setError(null);

            // Build query parameters
            const params = new URLSearchParams();

            if (selectedCategory !== 'all') {
                params.append('categories', selectedCategory);
            }

            if (debouncedSearchTerm.trim()) {
                params.append('search', debouncedSearchTerm.trim());
            }

            const queryString = params.toString();
            const url = `${apiUrl}/api/bookstore/books/${queryString ? `?${queryString}` : ''}`;

            const booksResponse = await axios.get(url);
            const booksData = booksResponse.data.results || booksResponse.data;

            const formattedBooks = booksData.map(book => ({
                id: book.id,
                title: book.name,
                price: parseFloat(book.price),
                discount: parseFloat(book.discount),
                discounted_price: parseFloat(book.discounted_price),
                image: book.image || null,
                author: book.author,
                publisher: book.publisher,
                description: book.description,
                total_amount: book.total_amount,
                categories: book.categories || [],
                category_names: book.categories ? book.categories.map(cat => cat.name) : []
            }));

            setBooks(formattedBooks);
            setFilteredBooks(formattedBooks);
            setCurrentPage(1); // Reset to first page when filtering
        } catch (error) {
            console.error('Error fetching books:', error);
            setError('Failed to load books. Please try again later.');
            toast.error('Failed to load books');
        } finally {
            setLoading(false);
            setSearchLoading(false);
        }
    };

    const indexOfLastBook = currentPage * booksPerPage;
    const indexOfFirstBook = indexOfLastBook - booksPerPage;
    const currentBooks = filteredBooks.slice(indexOfFirstBook, indexOfLastBook);
    const totalPages = Math.ceil(filteredBooks.length / booksPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleRefresh = () => {
        setSearchTerm('');
        setDebouncedSearchTerm('');
        setSelectedCategory('all');
        setCurrentPage(1);
        fetchBooks();
    };

    const handleCategoryChange = (categoryId) => {
        setSelectedCategory(categoryId);
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const clearFilters = () => {
        setSearchTerm('');
        setDebouncedSearchTerm('');
        setSelectedCategory('all');
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500 mb-4"></div>
                    <p className="text-gray-600 text-lg font-medium">Loading books...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
                <div className="text-center">
                    <div className="text-red-500 mb-4">
                        <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Books</h3>
                    <p className="text-gray-500 mb-4">{error}</p>
                    <button
                        onClick={handleRefresh}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 text-sm font-medium"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4 sm:px-6 lg:px-8 mt-20">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
                    <div>
                        <h1 className="text-4xl font-bold text-gray-900 mb-2">Book Store</h1>
                        <p className="text-gray-600">Discover amazing books from our collection</p>
                    </div>
                </div>

                {/* Search and Filter Section */}
                <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
                    {/* Search Bar */}
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                        <div className="mb-6">
                            <div className="relative max-w-4xl mx-auto">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                {searchLoading && (
                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-blue-500"></div>
                                    </div>
                                )}
                                <input
                                    type="text"
                                    placeholder="Search books by title, author, or publisher..."
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                />
                            </div>
                        </div>

                        {/* Category Filter Buttons */}
                        <div className="mb-4">
                            <div className="flex flex-wrap gap-2">
                                <button
                                    onClick={() => handleCategoryChange('all')}
                                    className={`px-4 py-2 rounded-full text-sm font-medium border-2 transition-all duration-200 ${selectedCategory === 'all'
                                            ? 'bg-blue-500 text-white border-blue-500'
                                            : 'bg-white text-gray-700 border-gray-300 hover:border-blue-300 hover:bg-blue-50'
                                        }`}
                                >
                                    সব
                                </button>
                                {categories.map(category => (
                                    <button
                                        key={category.id}
                                        onClick={() => handleCategoryChange(category.id)}
                                        className={`px-4 py-2 rounded-full text-sm font-medium border-2 transition-all duration-200 ${selectedCategory === category.id.toString()
                                                ? 'bg-red-500 text-white border-red-500'
                                                : 'bg-white text-gray-700 border-red-300 hover:border-red-400 hover:bg-red-50'
                                            }`}
                                    >
                                        {category.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Active Filters Display */}
                    {(debouncedSearchTerm || selectedCategory !== 'all') && (
                        <div className="mt-6 pt-6 border-t border-gray-200">
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="text-sm font-medium text-gray-700">Active filters:</span>
                                {debouncedSearchTerm && (
                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                                        Search: "{debouncedSearchTerm}"
                                        <button
                                            onClick={() => {
                                                setSearchTerm('');
                                                setDebouncedSearchTerm('');
                                            }}
                                            className="ml-2 text-blue-600 hover:text-blue-800"
                                        >
                                            ×
                                        </button>
                                    </span>
                                )}
                                {selectedCategory !== 'all' && (
                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
                                        Category: {categories.find(cat => cat.id === parseInt(selectedCategory))?.name}
                                        <button
                                            onClick={() => setSelectedCategory('all')}
                                            className="ml-2 text-green-600 hover:text-green-800"
                                        >
                                            ×
                                        </button>
                                    </span>
                                )}
                                <button
                                    onClick={clearFilters}
                                    className="text-sm text-red-600 hover:text-red-800 font-medium"
                                >
                                    Clear all
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Results Summary */}
                <div className="mb-6">
                    <p className="text-gray-600">
                        Showing {currentBooks.length} of {filteredBooks.length} books
                        {selectedCategory !== 'all' && (
                            <span> in category "{categories.find(cat => cat.id === parseInt(selectedCategory))?.name}"</span>
                        )}
                        {debouncedSearchTerm && <span> matching "{debouncedSearchTerm}"</span>}
                    </p>
                </div>

                {/* Books Grid */}
                {currentBooks.length === 0 ? (
                    <div className="text-center py-12">
                        <div className="text-gray-500 mb-4">
                            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Books Found</h3>
                        <p className="text-gray-500 mb-4">Try adjusting your search or filter criteria.</p>
                        <button
                            onClick={clearFilters}
                            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 text-sm font-medium"
                        >
                            Clear Filters
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
                            {currentBooks.map((book) => (
                                <div key={book.id} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group">
                                    <div className="relative">
                                        {book.image ? (
                                            <img
                                                src={book.image}
                                                alt={book.title}
                                                className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                                                onError={(e) => {
                                                    e.target.src = '/images/book-placeholder.jpg';
                                                }}
                                            />
                                        ) : (
                                            <div className="w-full h-64 bg-gray-100 flex items-center justify-center">
                                                <span className="text-gray-400 text-sm">No Image</span>
                                            </div>
                                        )}
                                        <div className="absolute top-3 right-3">
                                            <span className={`px-3 py-1 text-xs font-semibold rounded-full ${book.total_amount === 0
                                                ? 'bg-red-100 text-red-800'
                                                : book.total_amount < 5
                                                    ? 'bg-yellow-100 text-yellow-800'
                                                    : 'bg-green-100 text-green-800'
                                                }`}>
                                                {book.total_amount === 0 ? 'Out of Stock' : `${book.total_amount} left`}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="p-6">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">{book.title}</h3>
                                        <div className="text-sm text-gray-600 mb-4 space-y-1">
                                            <p>By: <span className="font-medium">{book.author}</span></p>
                                            <p>Publisher: <span className="font-medium">{book.publisher}</span></p>
                                            {book.categories && book.categories.length > 0 && (
                                                <div className="flex flex-wrap gap-1 mt-2">
                                                    {book.categories.map((category, index) => (
                                                        <span
                                                            key={index}
                                                            className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800"
                                                        >
                                                            {category.name}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <div>
                                                {book.discount > 0 ? (
                                                    <div>
                                                        <span className="text-gray-400 line-through text-sm">
                                                            ৳{book.price.toFixed(2)}
                                                        </span>
                                                        <p className="text-pink-600 font-bold text-lg">
                                                            ৳{book.discounted_price.toFixed(2)}
                                                        </p>
                                                    </div>
                                                ) : (
                                                    <p className="text-pink-600 font-bold text-lg">
                                                        ৳{book.price.toFixed(2)}
                                                    </p>
                                                )}
                                            </div>
                                            <Link
                                                to={`/book-store/${book.id}`}
                                                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-all duration-200 transform hover:scale-105"
                                            >
                                                View Details
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-center space-x-2">
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <ChevronLeft className="w-4 h-4 mr-1" />
                                    Previous
                                </button>

                                {/* Page Numbers */}
                                <div className="flex space-x-1">
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        let pageNumber;
                                        if (totalPages <= 5) {
                                            pageNumber = i + 1;
                                        } else if (currentPage <= 3) {
                                            pageNumber = i + 1;
                                        } else if (currentPage >= totalPages - 2) {
                                            pageNumber = totalPages - 4 + i;
                                        } else {
                                            pageNumber = currentPage - 2 + i;
                                        }

                                        return (
                                            <button
                                                key={pageNumber}
                                                onClick={() => handlePageChange(pageNumber)}
                                                className={`px-3 py-2 text-sm font-medium rounded-lg ${currentPage === pageNumber
                                                    ? 'bg-blue-600 text-white'
                                                    : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                                    }`}
                                            >
                                                {pageNumber}
                                            </button>
                                        );
                                    })}
                                </div>

                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next
                                    <ChevronRight className="w-4 h-4 ml-1" />
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default BookStore;